import { type PropertyListItemDto, type RentalAgreementDto } from '../../services/api';
import { i18nService, t } from '../../services/i18n';
import { buildPropertyDetailsUrl } from '../property/property-navigation';

const MONTHS_BACK = 3;
const MONTHS_FORWARD = 15;
const ENDING_SOON_DAYS = 90;

function parseDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function toPercent(date: Date, windowStart: Date, windowEnd: Date): number {
  const total = windowEnd.getTime() - windowStart.getTime();
  if (total === 0) return 0;
  return clamp(((date.getTime() - windowStart.getTime()) / total) * 100, 0, 100);
}

function monthLabel(date: Date): string {
  return date.toLocaleDateString(i18nService.dateLocale, { month: 'short', year: '2-digit' });
}

interface ContractBar {
  agreement: RentalAgreementDto;
  startPct: number;
  widthPct: number;
  colorClass: string;
  label: string;
  tooltip: string;
  href: string;
}

interface PropertyRow {
  property: PropertyListItemDto;
  bars: ContractBar[];
  isEmpty: boolean;
}

function classifyAgreement(agreement: RentalAgreementDto, today: Date): string {
  if (agreement.endDate) {
    const end = parseDate(agreement.endDate);
    const daysLeft = Math.ceil((end.getTime() - today.getTime()) / 86400000);
    if (daysLeft <= 0) return 'ct-bar--ended';
    if (daysLeft <= ENDING_SOON_DAYS && agreement.status === 'Actief') return 'ct-bar--ending-soon';
  }

  if (agreement.status !== 'Actief') return 'ct-bar--inactive';
  return 'ct-bar--active';
}

function overlapsWindow(agreement: RentalAgreementDto, windowStart: Date, windowEnd: Date): boolean {
  if (!agreement.startDate) {
    return false;
  }

  const start = parseDate(agreement.startDate);
  if (start > windowEnd) {
    return false;
  }

  if (!agreement.endDate) {
    return true;
  }

  return parseDate(agreement.endDate) >= windowStart;
}

function buildRows(
  properties: PropertyListItemDto[],
  agreements: RentalAgreementDto[],
  windowStart: Date,
  windowEnd: Date,
  today: Date,
): PropertyRow[] {
  const byProperty = new Map<string, RentalAgreementDto[]>();
  for (const agreement of agreements) {
    const list = byProperty.get(agreement.propertyId) ?? [];
    list.push(agreement);
    byProperty.set(agreement.propertyId, list);
  }

  return properties.map((property) => {
    const propAgreements = byProperty.get(property.id) ?? [];
    const visibleAgreements = propAgreements
      .filter((agreement) => overlapsWindow(agreement, windowStart, windowEnd))
      .sort((left, right) => {
        const leftDate = left.startDate ? parseDate(left.startDate).getTime() : 0;
        const rightDate = right.startDate ? parseDate(right.startDate).getTime() : 0;
        return leftDate - rightDate;
      });

    const bars: ContractBar[] = visibleAgreements
      .filter((agreement) => agreement.startDate)
      .map((agreement) => {
        const start = parseDate(agreement.startDate!);
        const end = agreement.endDate ? parseDate(agreement.endDate) : windowEnd;

        const startPct = toPercent(start, windowStart, windowEnd);
        const endPct = toPercent(end, windowStart, windowEnd);
        const widthPct = Math.max(endPct - startPct, 0.5);
        const colorClass = classifyAgreement(agreement, today);

        const startLabel = i18nService.formatDate(agreement.startDate!, { day: 'numeric', month: 'short', year: 'numeric' });
        const endLabel = agreement.endDate
          ? i18nService.formatDate(agreement.endDate, { day: 'numeric', month: 'short', year: 'numeric' })
          : t('contractTimeline.openEnded');
        const tooltip = `${agreement.tenantName} · ${startLabel} → ${endLabel}`;

        return {
          agreement,
          startPct,
          widthPct,
          colorClass,
          label: agreement.tenantName,
          tooltip,
          href: `/tenants/?tenant=${encodeURIComponent(agreement.tenantId)}&property=${encodeURIComponent(agreement.propertyId)}`,
        };
      });

    return { property, bars, isEmpty: bars.length === 0 };
  });
}

function renderMonthMarkers(windowStart: Date, windowEnd: Date): string {
  const markers: string[] = [];
  const cursor = new Date(windowStart.getFullYear(), windowStart.getMonth(), 1);

  while (cursor <= windowEnd) {
    const pct = toPercent(cursor, windowStart, windowEnd);
    const label = monthLabel(cursor);
    markers.push(`<div class="ct-month-marker" style="left:${pct.toFixed(2)}%"><span>${label}</span></div>`);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return markers.join('');
}

function renderRow(row: PropertyRow): string {
  const propertyHref = buildPropertyDetailsUrl(row.property.id);
  const addressHtml = `<a href="${propertyHref}" class="ct-row-address">${escHtml(row.property.address)}</a>`;

  if (row.isEmpty) {
    return `
      <div class="ct-row ct-row--empty">
        <div class="ct-row-label">${addressHtml}</div>
        <div class="ct-row-track">
          <div class="ct-bar ct-bar--vacant" title="${escAttr(t('contractTimeline.vacant'))}">
            <span class="ct-bar-label">${t('contractTimeline.vacant')}</span>
          </div>
        </div>
      </div>`;
  }

  const barsHtml = row.bars.map((bar) => `
    <a class="ct-bar ${bar.colorClass}"
       href="${bar.href}"
       style="left:${bar.startPct.toFixed(2)}%;width:${bar.widthPct.toFixed(2)}%"
       title="${escAttr(bar.tooltip)}">
      <span class="ct-bar-label">${escHtml(bar.label)}</span>
    </a>`).join('');

  return `
    <div class="ct-row">
      <div class="ct-row-label">${addressHtml}</div>
      <div class="ct-row-track">${barsHtml}</div>
    </div>`;
}

function escHtml(str: string): string {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

function escAttr(str: string): string {
  return str.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export class RcContractTimelineView extends HTMLElement {
  properties: PropertyListItemDto[] = [];
  agreements: RentalAgreementDto[] = [];

  render(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const windowStart = addMonths(today, -MONTHS_BACK);
    windowStart.setDate(1);
    const windowEnd = addMonths(today, MONTHS_FORWARD);

    const rows = buildRows(this.properties, this.agreements, windowStart, windowEnd, today);

    if (rows.length === 0) {
      this.innerHTML = `
        <div class="contract-timeline-page">
          <div class="ct-header">
            <h1>${t('contractTimeline.title')}</h1>
            <p class="ct-subtitle">${t('contractTimeline.subtitle')}</p>
          </div>
          <div class="empty-state">
            <p>${t('contractTimeline.noProperties')}</p>
            <a href="/properties/" class="btn btn-primary">${t('contractTimeline.addProperty')}</a>
          </div>
        </div>`;
      return;
    }

    const todayPct = toPercent(today, windowStart, windowEnd);
    const legend = `
      <div class="ct-legend">
        <span class="ct-legend-item"><span class="ct-legend-dot ct-bar--active"></span>${t('contractTimeline.legend.active')}</span>
        <span class="ct-legend-item"><span class="ct-legend-dot ct-bar--ending-soon"></span>${t('contractTimeline.legend.endingSoon')}</span>
        <span class="ct-legend-item"><span class="ct-legend-dot ct-bar--ended"></span>${t('contractTimeline.legend.ended')}</span>
        <span class="ct-legend-item"><span class="ct-legend-dot ct-bar--inactive"></span>${t('contractTimeline.legend.inactive')}</span>
        <span class="ct-legend-item"><span class="ct-legend-dot ct-bar--vacant"></span>${t('contractTimeline.legend.vacant')}</span>
      </div>`;

    this.innerHTML = `
      <div class="contract-timeline-page">
        <div class="ct-header">
          <div>
            <h1>${t('contractTimeline.title')}</h1>
            <p class="ct-subtitle">${t('contractTimeline.subtitle')}</p>
          </div>
          ${legend}
        </div>
        <div class="ct-chart card">
          <div class="ct-axis">
            ${renderMonthMarkers(windowStart, windowEnd)}
            <div class="ct-today-line" style="left:${todayPct.toFixed(2)}%" title="${t('contractTimeline.today')}">
              <span class="ct-today-label">${t('contractTimeline.today')}</span>
            </div>
          </div>
          <div class="ct-rows">
            ${rows.map(renderRow).join('')}
          </div>
        </div>
      </div>`;
  }
}

if (!customElements.get('rc-contract-timeline-view')) {
  customElements.define('rc-contract-timeline-view', RcContractTimelineView);
}
